import {useState, useEffect, useCallback, useMemo} from 'react';
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {useActiveTiers} from '@/containers/ClubPlus/hooks/useActiveTiers';
import {useAppDispatch, useAppSelector} from '@/store';
import {setToastNotifications} from '@/store/slices/commonSlice';
import {selectCountryCode} from '@/store/slices/countryCodeSlice';
import {useIntl} from 'react-intl';
import {getLowerRange} from '@/containers/ClubPlus/Tabs/Overview/TiersChart/TiersChart.utils';
import {currencyMap} from '@/utils/commons';
import {calculateNextEvaluationDate} from '@/utils/tierEvaluationDateUtils';
import {
  getTierAutomationConfig,
  saveTierAutomationConfig,
  TIER_QUERY_KEY
} from '@/services/ClubPlus/TierService';
import {useDropdown} from '@/hooks/Dropdown-hook';
import {DROPDOWN_TYPES} from '@/utils/constants/dropdownConstants';

const EVALUATION_EXECUTION_LABELS = {
  WEEKLY: 'End of week',
  BIWEEKLY: 'End of bi-weekly period',
  MONTHLY: 'End of month'
};

const formatFrequency = (value) => {
  if (!value) return '';
  return value
    .toLowerCase()
    .replaceAll('_', ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatLastUpdated = (isoString) => {
  if (!isoString) return '-';
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return '-';
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${mm}-${dd}-${yyyy}`;
};

const getTierRangesFromApi = (tiers, tierRanges) => {
  if (!tiers || !tierRanges || tierRanges.length === 0) return [];

  const rankMap = new Map();
  tierRanges.forEach((tr) => {
    const id = tr.tierId ?? tr.tierID;
    if (id !== undefined) rankMap.set(id, tr.rank ?? 0);
  });

  const sortedTiers = [...tiers].sort((a, b) => {
    const rankA = rankMap.get(a.tierID) ?? 0;
    const rankB = rankMap.get(b.tierID) ?? 0;
    return rankA - rankB;
  });

  const seenTierIds = new Set();
  const uniqueTiers = sortedTiers.filter((tier) => {
    if (seenTierIds.has(tier.tierID)) {
      return false;
    }
    seenTierIds.add(tier.tierID);
    return true;
  });

  return uniqueTiers.map((tier) => {
    const tr = tierRanges.find(
      (range) => range.tierId === tier.tierID || range.tierID === tier.tierID
    );
    return {
      tierID: tier.tierID,
      name: tier.name,
      color: tier.color || '',
      upperRange: tr?.upperRange ?? null
    };
  });
};

export const useTierConfigurations = (autoEdit = false) => {
  const {tiers} = useActiveTiers();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const countryCode = useAppSelector(selectCountryCode);
  const currencyCode = currencyMap[countryCode] || 'USD';
  const {formatMessage} = useIntl();

  const automationConfigQuery = useQuery({
    queryKey: [TIER_QUERY_KEY.TIER_AUTOMATION_CONFIG],
    queryFn: getTierAutomationConfig
  });

  const automationConfig = automationConfigQuery.data?.data;

  const {dropdownList: salesPerformanceDropdownList = []} = useDropdown({
    dropdownType: DROPDOWN_TYPES.SalesPerformance
  });

  const {dropdownList: evaluationFrequencyDropdownList = []} = useDropdown({
    dropdownType: DROPDOWN_TYPES.EvaluationFrequency
  });

  const salesMethodLabelMap = useMemo(() => {
    const map = {};
    salesPerformanceDropdownList.forEach((item) => {
      map[item.value] = item.displayText;
    });
    return map;
  }, [salesPerformanceDropdownList]);

  const evaluationFrequencyLabelMap = useMemo(() => {
    const map = {};
    evaluationFrequencyDropdownList.forEach((item) => {
      map[item.value] = item.displayText;
    });
    return map;
  }, [evaluationFrequencyDropdownList]);

  const [ranges, setRanges] = useState([]);
  const [originalRanges, setOriginalRanges] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [errors, setErrors] = useState({});
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [autoEditTriggered, setAutoEditTriggered] = useState(false);

  const [salesMethod, setSalesMethod] = useState('');
  const [evaluationFrequency, setEvaluationFrequency] = useState('');
  const [salesDays, setSalesDays] = useState('');
  const [includeOfflineOrders, setIncludeOfflineOrders] = useState(false);

  useEffect(() => {
    if (tiers && tiers.length > 0 && automationConfig?.tierRanges?.length > 0) {
      const newRanges = getTierRangesFromApi(
        tiers,
        automationConfig.tierRanges
      );
      setRanges(newRanges);
      setOriginalRanges(newRanges);
      if (autoEdit && !autoEditTriggered) {
        setIsEditMode(true);
        setAutoEditTriggered(true);
      }
    }
  }, [tiers, automationConfig, autoEdit, autoEditTriggered]);

  useEffect(() => {
    if (automationConfig) {
      setSalesMethod(automationConfig.salesMethod || '');
      setEvaluationFrequency(automationConfig.evaluationFrequency || '');
      setSalesDays(String(automationConfig.salesDays ?? ''));
      setIncludeOfflineOrders(!!automationConfig.includeOfflineOrders);
    }
  }, [automationConfig]);

  const hasTierConfiguration = useMemo(() => {
    const tierRanges = automationConfig?.tierRanges;
    return tierRanges?.length > 0;
  }, [automationConfig]);

  const derivedRanges = useMemo(() => {
    return ranges.map((tier, index) => {
      let lowerRange;
      if (index === 0) {
        lowerRange = 0;
      } else {
        lowerRange = getLowerRange(ranges[index - 1].upperRange);
      }
      const upperRange = index === ranges.length - 1 ? null : tier.upperRange;
      return {...tier, lowerRange, upperRange};
    });
  }, [ranges]);

  const isValidUpper = useCallback(
    (upper) => upper !== null && upper !== undefined && !isNaN(upper),
    []
  );

  const validateRanges = useCallback(
    (currentRanges) => {
      const newErrors = {};
      const upperValues = currentRanges.map((r) => r.upperRange);

      const getRange = (ranges, idx) => {
        const upper = ranges[idx].upperRange;
        const lower = idx === 0 ? 0 : getLowerRange(ranges[idx - 1].upperRange);
        return {upper, lower};
      };

      const hasOverlap = (upper, lower, otherUpper, otherLower) =>
        isValidUpper(upper) &&
        isValidUpper(otherUpper) &&
        lower !== null &&
        otherLower !== null &&
        upper > otherLower &&
        lower < otherUpper;

      currentRanges.forEach((tier, index) => {
        if (index === currentRanges.length - 1) return;

        const {upper, lower} = getRange(currentRanges, index);

        if (!isValidUpper(upper)) {
          newErrors[index] = formatMessage({
            id: 'CLUB_PLUS.TIER_CONFIGURATIONS_VALIDATION_INVALID_RANGE'
          });
          return;
        }

        if (upper < 0 || !Number.isInteger(upper)) {
          newErrors[index] = formatMessage({
            id: 'CLUB_PLUS.TIER_CONFIGURATIONS_VALIDATION_INVALID_RANGE'
          });
          return;
        }

        if (upper <= lower) {
          newErrors[index] = formatMessage({
            id: 'CLUB_PLUS.TIER_CONFIGURATIONS_VALIDATION_INVALID_RANGE'
          });
          return;
        }

        const hasDuplicate = upperValues.some(
          (v, i) => isValidUpper(v) && v === upper && i !== index
        );
        if (hasDuplicate) {
          newErrors[index] = formatMessage({
            id: 'CLUB_PLUS.TIER_CONFIGURATIONS_VALIDATION_DUPLICATE'
          });
          return;
        }

        const overlapFound = currentRanges.some((_, i) => {
          if (i === index) return false;
          const {upper: otherUpper, lower: otherLower} = getRange(
            currentRanges,
            i
          );
          return hasOverlap(upper, lower, otherUpper, otherLower);
        });

        if (overlapFound) {
          newErrors[index] = formatMessage({
            id: 'CLUB_PLUS.TIER_CONFIGURATIONS_VALIDATION_OVERLAP'
          });
        }
      });

      return newErrors;
    },
    [formatMessage, isValidUpper]
  );

  useEffect(() => {
    if (isEditMode && ranges.length > 0) {
      const newErrors = validateRanges(ranges);
      setErrors(newErrors);
    }
  }, [isEditMode, ranges, validateRanges]);

  const {mutate, isPending: isSaving} = useMutation({
    mutationFn: async (payload) => {
      const latestConfig = await getTierAutomationConfig();
      const latestVersion = latestConfig?.data?.version ?? 0;
      return saveTierAutomationConfig({
        ...payload,
        version: latestVersion
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [TIER_QUERY_KEY.TIER_AUTOMATION_CONFIG]
      });
      dispatch(
        setToastNotifications({
          show: true,
          message: formatMessage({
            id: 'CLUB_PLUS.TIER_CONFIGURATIONS_SAVE_SUCCESS'
          }),
          type: 'success'
        })
      );
      setIsEditMode(false);
      setOriginalRanges(ranges);
    },
    onError: () => {
      dispatch(
        setToastNotifications({
          show: true,
          message: formatMessage({
            id: 'CLUB_PLUS.TIER_CONFIGURATIONS_SAVE_ERROR'
          }),
          type: 'error'
        })
      );
    }
  });

  const handleEdit = useCallback(() => {
    setIsEditMode(true);
    setOriginalRanges(ranges);
  }, [ranges]);

  const handleCancel = useCallback(() => {
    setIsEditMode(false);
    setRanges(originalRanges);
    setErrors({});
    if (automationConfig) {
      setSalesMethod(automationConfig.salesMethod || '');
      setEvaluationFrequency(automationConfig.evaluationFrequency || '');
      setSalesDays(String(automationConfig.salesDays ?? ''));
      setIncludeOfflineOrders(!!automationConfig.includeOfflineOrders);
    }
  }, [originalRanges, automationConfig]);

  const handleUpperRangeChange = useCallback(
    (index, value) => {
      const numValue = value === '' ? null : parseFloat(value);
      const newRanges = ranges.map((r, i) => {
        if (i === index) {
          return {...r, upperRange: numValue};
        }
        return r;
      });

      const newErrors = validateRanges(newRanges);
      setRanges(newRanges);
      setErrors(newErrors);
    },
    [ranges, validateRanges]
  );

  const buildPayload = useCallback(() => {
    return {
      salesMethod,
      evaluationFrequency,
      salesDays: Number.parseInt(salesDays, 10) || 0,
      includeOfflineOrders,
      tierRanges: derivedRanges.map((t) => ({
        tierId: t.tierID,
        lowerRange: t.lowerRange,
        upperRange: t.upperRange
      }))
    };
  }, [
    salesMethod,
    evaluationFrequency,
    salesDays,
    includeOfflineOrders,
    derivedRanges
  ]);

  const isMarketConfigValid = useMemo(() => {
    const hasSalesMethod = salesMethod.trim() !== '';
    const hasEvaluationFrequency = evaluationFrequency.trim() !== '';
    const normalizedSalesDays = String(salesDays ?? '').trim();
    const parsedSalesDays = Number.parseInt(normalizedSalesDays, 10);
    const hasValidSalesDays =
      /^\d+$/.test(normalizedSalesDays) && parsedSalesDays > 0;

    return hasSalesMethod && hasEvaluationFrequency && hasValidSalesDays;
  }, [salesMethod, evaluationFrequency, salesDays]);

  const handleSave = useCallback(() => {
    const newErrors = validateRanges(ranges);
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0 || !isMarketConfigValid) {
      return;
    }
    setShowSaveModal(true);
  }, [ranges, validateRanges, isMarketConfigValid]);

  const handleConfirmSave = useCallback(() => {
    setShowSaveModal(false);
    mutate(buildPayload());
  }, [mutate, buildPayload]);

  const handleCancelSave = useCallback(() => {
    setShowSaveModal(false);
    handleCancel();
  }, [handleCancel]);

  const hasErrors = Object.keys(errors).length > 0;

  const marketConfigValues = useMemo(() => {
    if (!automationConfig) return null;
    return {
      salesMethod: salesMethodLabelMap[salesMethod] || salesMethod || '-',
      evaluationFrequency:
        evaluationFrequencyLabelMap[evaluationFrequency] ||
        formatFrequency(evaluationFrequency) ||
        '-',
      evaluationExecution:
        EVALUATION_EXECUTION_LABELS[evaluationFrequency] || '-',
      salesDays: String(salesDays ?? ''),
      includeOfflineOrders: includeOfflineOrders ? 'Yes' : 'No',
      salesMethodValue: salesMethod,
      evaluationFrequencyValue: evaluationFrequency,
      salesDaysValue: salesDays,
      includeOfflineOrdersValue: includeOfflineOrders
    };
  }, [
    automationConfig,
    salesMethod,
    evaluationFrequency,
    salesDays,
    includeOfflineOrders,
    salesMethodLabelMap,
    evaluationFrequencyLabelMap
  ]);

  return {
    tiers: derivedRanges,
    isEditMode,
    errors,
    hasErrors,
    isMarketConfigValid,
    hasTierConfiguration,
    lastUpdated: formatLastUpdated(automationConfig?.lastUpdatedAt),
    currencyCode,
    marketConfigValues,
    nextEvaluationDate: calculateNextEvaluationDate(evaluationFrequency),
    isLoadingAutomationConfig: automationConfigQuery.isLoading,
    isSaving,
    showSaveModal,
    handleEdit,
    handleCancel,
    handleSave,
    handleConfirmSave,
    handleCancelSave,
    handleUpperRangeChange,
    setSalesMethod,
    setEvaluationFrequency,
    setSalesDays,
    setIncludeOfflineOrders,
    salesPerformanceDropdownList,
    evaluationFrequencyDropdownList
  };
};
